import { Alert, AlertIcon, Box, Flex, Text } from '@chakra-ui/react'

import { FormField } from '../../../../components/FormField'
import { FormSelect } from '../../../../components/Select'
import { Tooltip } from '../../../../components/Tooltip'
import { SelectEvent, Ticket } from '../../../../services/Event/types'
import { IContestFormTabProps } from './types'

interface IProps extends IContestFormTabProps {
  events: SelectEvent[]
  hasPaidApplications: boolean
}

/** Título do ingresso com o sufixo de infantil, para o tipo ficar visível antes de escolher. */
const ticketLabel = (ticket: Ticket) => `${ticket.title}${ticket.is_child_ticket ? ' — Infantil' : ''}`

export function EventTab({ form, setForm, errors, saving, events, hasPaidApplications }: IProps) {
  const ticketsOfSelectedEvent = events
    .filter((event) => event.tickets && event.id === form.event_id)
    .flatMap((event) => event.tickets)

  return (
    <Flex direction="column" gap={3}>
      <Box>
        <FormField label="Evento vinculado ao concurso" htmlFor="event_id" error={errors.event_id} isRequired>
          <FormSelect
            id="event_id"
            isDisabled={saving || hasPaidApplications}
            value={form.event_id}
            onChange={(value) => setForm({ ...form, event_id: Number(value) })}
            options={events.map((event) => ({ value: event.id, label: event.name }))}
            placeholder="Selecione o evento vinculado ao concurso"
          />
        </FormField>
      </Box>

      {form.event_id > 0 && (
        <Box>
          <FormField
            label="Ingresso destinado aos participantes do concurso"
            htmlFor="contest_participants_ticket_id"
            error={errors.contest_participants_ticket_id}
            isRequired
          >
            <FormSelect
              id="contest_participants_ticket_id"
              isDisabled={saving || hasPaidApplications}
              value={form.contest_participants_ticket_id}
              onChange={(value) => setForm({ ...form, contest_participants_ticket_id: Number(value) })}
              options={ticketsOfSelectedEvent.map((ticket) => ({
                value: ticket.id,
                label: `${ticketLabel(ticket)} - Quantidade: ${ticket.quantity}`
              }))}
              placeholder="Selecione o ingresso destinado aos participantes do concurso"
            />
          </FormField>
        </Box>
      )}

      {form.event_id > 0 && (
        <Box>
          <FormField
            label={
              <Tooltip label="Cada inscrito recebe um cupom com 2 usos deste ingresso, enviado assim que a inscrição é paga, para levar convidados. O ingresso do próprio participante é gerado à parte, pelo campo acima.">
                <Text as="span" cursor="help" textDecoration="underline dotted">
                  Ingresso para os convidados dos participantes do concurso (cupom automático)
                </Text>
              </Tooltip>
            }
            htmlFor="guest_ticket_id"
            error={errors.guest_ticket_id}
            helperText={
              ticketsOfSelectedEvent.some((ticket) => ticket.is_child_ticket)
                ? 'Ingresso infantil não pode ser o de convidado: o cupom é para os acompanhantes do inscrito.'
                : undefined
            }
          >
            <FormSelect
              id="guest_ticket_id"
              isDisabled={saving || hasPaidApplications}
              value={form.guest_ticket_id ?? ''}
              onChange={(value) =>
                setForm({
                  ...form,
                  guest_ticket_id: value ? Number(value) : null
                })
              }
              // Ingresso infantil fica visível mas não escolhível: a API recusa com 422, e
              // desabilitar a opção evita o erro em vez de só reportá-lo.
              options={ticketsOfSelectedEvent.map((ticket) => ({
                value: ticket.id,
                label: ticketLabel(ticket),
                isDisabled: ticket.is_child_ticket
              }))}
              placeholder="Nenhum ingresso de convidado"
            />
          </FormField>
        </Box>
      )}

      {hasPaidApplications && (
        <Alert status="info" rounded={'lg'}>
          <AlertIcon />
          Não é possível editar o evento e ingresso vinculado aos inscritos do concurso após o primeiro participante se
          inscrever.
        </Alert>
      )}
    </Flex>
  )
}
